/**
 * Fetches today's order-processing statistics from Firestore and returns
 * a normalized {@link ProcessedOrdersData} object.
 * @module fetchProcessedOrdersData
 */

import moment from "moment";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../api/firebase/references";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/** Aggregated processing stats for the current day's orders. */
export interface ProcessedOrdersData {
  completion_percentage: number;
  orders_processed: number;
  orders_remaining: number;
  time_taken: string;
  loading: boolean;
  skipped_duplicate_order_ids: string[];
}

/**
 * Reads the processed-orders document for today from Firestore and computes
 * time_taken as the diff between `created_at` and `updated_at`.
 *
 * @returns Resolved stats or a zeroed-out fallback on error.
 */
export const fetchProcessedOrdersData =
  async (): Promise<ProcessedOrdersData> => {
    try {
      const orgId = getOrgIdForFetchExternalWorklist();

      if (orgId === "") {
        return {
          completion_percentage: 0,
          orders_processed: 0,
          orders_remaining: 0,
          time_taken: "",
          loading: false,
          skipped_duplicate_order_ids: [],
        };
      }

      const currentDateInDashedFormat = moment().format("YYYY-MM-DD");
      const data = await FirestoreService.getDocument(
        FirestoreDocumentReference.processedOrdersToday(
          orgId,
          currentDateInDashedFormat,
        ),
      );

      const completion_percentage =
        data?.["processing_progress"]?.["completion_percentage"] || 0;
      const orders_processed =
        data?.["processing_progress"]?.["orders_processed"] || 0;
      const orders_remaining =
        data?.["processing_progress"]?.["orders_remaining"] || 0;
      const created_at = data?.["created_at"] || "";
      const updated_at = data?.["updated_at"] || "";
      const skipped_duplicate_order_ids =
        data?.["skipped_duplicate_order_ids"] || [];
      return {
        completion_percentage,
        orders_processed,
        orders_remaining,
        time_taken:
          moment(updated_at).diff(moment(created_at), "minutes") + " mins",
        loading: false,
        skipped_duplicate_order_ids,
      };
    } catch (error) {
      console.error("Error fetching processed orders data:", error);
      return {
        completion_percentage: 0,
        orders_processed: 0,
        orders_remaining: 0,
        time_taken: "",
        loading: false,
        skipped_duplicate_order_ids: [],
      };
    }
  };
